/**
 * Projection for `DataBrowserScene` — the demo's file tree (`DOCTREE` at demo
 * line 2408, built at 2411–2417) and its reference resolver (`openDoc`, demo
 * line 2454).
 *
 * Two jobs, both pure:
 *
 * 1. **Build the tree.** `shared/seed/doctree.ts` carries thirteen literal
 *    folders plus one rule, and deliberately carries no file names for the
 *    generated folders — they were `.map()` calls over the data pack. This
 *    file turns that description into an actual list of buttons, resolving
 *    `{ countOf }` and `{ machineAlias }` label placeholders on the way.
 * 2. **Resolve a reference.** Four other surfaces link here with `?doc=` —
 *    alerts, incidents, the assistant's source chips and the Asset360 tiles —
 *    and between them they use six different reference spellings. One resolver
 *    understands all of them.
 *
 * ## The `tab:` prefix bug, and the decision about it
 *
 * The demo builds a document key by **concatenating** the prefix and the
 * label, so `'tab:fleet'` + `'fleet_register.csv'` yields
 * `'tab:fleetfleet_register.csv'`, and `docView` then looks up
 * `SRC['fleetfleet_register.csv']`, which does not exist. All twelve `tab:`
 * buttons in the demo therefore toast "no preview built" instead of opening.
 * `doctree.ts` ports the prefixes exactly as written and leaves the decision
 * to this layer (its `TODO(P3)`).
 *
 * **The port keys them off the prefix suffix** — `'tab:fleet'` → the `fleet`
 * dataset — because the prefixes name real datasets, the viewer for them is
 * the same table viewer every other row uses, and shipping twelve buttons that
 * are known to do nothing is not faithfulness, it is a transcribed bug.
 *
 * Transformation layer (layer 3). No React, no fetching, no clock.
 *
 * @see src/versions/v1/modules/integration/DataBrowserScene.tsx
 * @see src/versions/v1/shared/seed/doctree.ts
 * @see docs/logic/integration/data.md
 */

import { IDS } from '../../data/core';
import type {
  CommsPayload,
  DocIndexPayload,
  LakeEntry,
  LakeIndex,
} from '../../schema/document';
import { groupNum } from '../../shared/oem-engine';
import {
  DOCTREE,
  type DocLabel,
  type DocTreeFolder,
} from '../../shared/seed/doctree';

// ---------------------------------------------------------------------------
// Tree shapes
// ---------------------------------------------------------------------------

/** One file button in the tree. */
export interface TreeFile {
  /** The document key — what `?doc=` carries and `loadDocView` parses. */
  readonly key: string;
  /** What is printed on the button. */
  readonly label: string;
  /** Upper-cased extension, the demo's `.tag-fmt` chip. */
  readonly ext: string;
  /** Machine ids the document mentions — the filter box searches these too. */
  readonly machines: readonly string[];
}

/** A `grp` node inside a connector folder. */
export interface TreeGroup {
  readonly id: string;
  readonly name: string;
  readonly files: readonly TreeFile[];
}

/** One top-level folder. */
export interface TreeFolder {
  readonly id: string;
  readonly name: string;
  /** Total files, groups included — the demo's count chip. */
  readonly count: number;
  /** Folders 1–3, 5 and 13 open on first paint, as the demo opens them. */
  readonly defaultOpen: boolean;
  readonly files: readonly TreeFile[];
  readonly groups: readonly TreeGroup[];
  /** Connector name, on the folders generated from the lake. Drives `?conn=`. */
  readonly connector?: string;
}

/** What {@link buildTree} needs loaded. */
export interface TreeCtx {
  readonly lake: LakeIndex;
  readonly comms: CommsPayload;
  readonly docIndex: DocIndexPayload;
  /** `WOS.length`, once `work-orders.json` has landed. `null` before that. */
  readonly workOrderCount: number | null;
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

/** File extension, lower-cased. The demo defaults a name with none to `csv`. */
export const extOf = (name: string): string =>
  (name.match(/\.(\w+)$/)?.[1] ?? 'csv').toLowerCase();

/**
 * Demo lines 2408–2410. Resolve a {@link DocLabel}'s segments.
 *
 * One label quotes a lazy dataset: `work_orders.csv — 3,920 orders`. The tree
 * does not fetch 792 KB of work orders to print a number, so until that domain
 * is in the cache the label is **truncated at the unresolved count** and
 * trimmed of the separator that introduced it — `work_orders.csv`. Printing an
 * em dash in place of the figure would read as a missing value rather than as
 * a pending one, and printing `— orders` reads as a bug.
 */
export function resolveLabel(label: DocLabel, ctx: Pick<TreeCtx, 'workOrderCount'>): string {
  const parts: string[] = [];

  for (const segment of label) {
    if ('text' in segment) {
      parts.push(segment.text);
      continue;
    }
    if ('countOf' in segment) {
      if (ctx.workOrderCount === null) break;
      parts.push(groupNum(ctx.workOrderCount));
      continue;
    }
    parts.push(IDS[segment.machineAlias] ?? segment.machineAlias);
  }

  return parts.join('').replace(/[\s—·,-]+$/, '');
}

/**
 * The key a file button carries.
 *
 * `'tab:<dataset>'` prefixes are used **as the key on their own** — see the
 * file header. Every other prefix is concatenated with the label, which is
 * what `docView` parses.
 *
 * The label used here is the **stable** one — resolved with no work-order
 * count — so a key cannot change under a button when that domain lands. The
 * only affected entry is the work-order CSV, whose viewer ignores the name.
 */
export function fileKey(prefix: string, label: DocLabel): string {
  return prefix.startsWith('tab:')
    ? prefix
    : `${prefix}${resolveLabel(label, { workOrderCount: null })}`;
}

// ---------------------------------------------------------------------------
// The tree
// ---------------------------------------------------------------------------

/** Folder indices the demo opens on first paint (`i<3||i===4||i===12`). */
const OPEN_BY_DEFAULT = new Set([0, 1, 2, 4, 12]);

/**
 * The five generated folders the extractor can still fill, plus the reports
 * folder, which it cannot.
 *
 * `B.report_files` was dropped at extract time with the rendered DOCX/PPTX
 * blobs (plan §A step 4), so the demo's file names are not recoverable. The
 * folder is kept with the five documents the assistant can rebuild, named for
 * what they are — see `loadDocView`'s `rep:` branch for what opening one does.
 */
const REPORT_ENTRIES: readonly { id: string; label: string }[] = [
  { id: 'surface', label: 'Weekly status report · Surface Drilling.docx' },
  { id: 'drills', label: 'Weekly status report · Underground Drilling.docx' },
  { id: 'loadhaul', label: 'Weekly status report · Load & Haul.docx' },
  { id: 'email_mi', label: 'Machine risk email · Mount Isolde.docx' },
  { id: 'deck_br', label: 'Broken Ridge equipment summary.pptx' },
  { id: 'audit', label: 'report_figures.json' },
];

/** Demo lines 2411–2414. The whole tree, in `DOCTREE` order. */
export function buildTree(ctx: TreeCtx): TreeFolder[] {
  return DOCTREE.map((folder, index) =>
    buildFolder(folder, index, ctx),
  ).flat();
}

function buildFolder(folder: DocTreeFolder, index: number, ctx: TreeCtx): TreeFolder[] {
  const defaultOpen = OPEN_BY_DEFAULT.has(index);

  if (folder.kind === 'static') {
    const files = folder.files.map((file) => {
      const label = resolveLabel(file.label, ctx);
      return {
        key: fileKey(file.prefix, file.label),
        label,
        ext: extOf(label),
        machines: [],
      };
    });
    return [
      {
        id: `folder-${index}`,
        name: folder.name,
        count: files.length,
        defaultOpen,
        files,
        groups: [],
      },
    ];
  }

  if (folder.kind === 'dataset') {
    const files = datasetFiles(folder.source.dataset, folder.source.folder, folder.prefix, ctx);
    return [
      {
        id: `folder-${index}`,
        name: folder.name,
        count: files.length,
        defaultOpen,
        files,
        groups: [],
      },
    ];
  }

  // `connectors` — one folder per distinct `conn`, in first-seen order.
  const byConnector = new Map<string, LakeEntry[]>();
  for (const entry of ctx.lake.entries) {
    const list = byConnector.get(entry.conn);
    if (list) list.push(entry);
    else byConnector.set(entry.conn, [entry]);
  }

  return [...byConnector.entries()].map(([connector, entries], i) => {
    const groupNames = [...new Set(entries.map((e) => e.group))];
    const toFile = (entry: LakeEntry): TreeFile => ({
      // Keyed by slug, not by file name: `LakeIndex.file_collisions` proves
      // file names are not unique, and a React key has to be.
      key: `x:${entry.file}`,
      label: entry.file,
      ext: entry.kind,
      machines: entry.machines ?? [],
    });

    const single = groupNames.length <= 1;
    return {
      id: `conn-${i}`,
      name: connector,
      count: entries.length,
      defaultOpen: false,
      connector,
      files: single ? entries.map(toFile) : [],
      groups: single
        ? []
        : groupNames.map((group) => ({
            id: `conn-${i}-${group || 'files'}`,
            name: group || 'Files',
            files: entries.filter((e) => e.group === group).map(toFile),
          })),
    };
  });
}

/** Demo lines 2408–2410. The five `.map()`-generated folders. */
function datasetFiles(
  dataset: string,
  folderFilter: string | undefined,
  prefix: string,
  ctx: TreeCtx,
): TreeFile[] {
  const file = (label: string, machines: readonly string[] = []): TreeFile => ({
    key: `${prefix}${label}`,
    label,
    ext: extOf(label),
    machines,
  });

  switch (dataset) {
    case 'B.docs':
      return ctx.docIndex.docs
        .filter((d) => !folderFilter || d.folder === folderFilter)
        .map((d) => file(d.file));
    case 'B.emails':
      return ctx.comms.emails.map((e) => file(e.file));
    case 'B.meetings':
      return ctx.comms.meetings.map((m) => file(m.transcript));
    case 'B.videos':
      return ctx.comms.videos.map((v) => file(v.transcript || v.video_id));
    case 'B.report_files':
      return REPORT_ENTRIES.map((r) => ({
        key: `rep:${r.id}`,
        label: r.label,
        ext: extOf(r.label),
        machines: [],
      }));
    default:
      return [];
  }
}

/** Total files across the tree — the demo's `#doccount` chip. */
export const treeFileCount = (tree: readonly TreeFolder[]): number =>
  tree.reduce((total, folder) => total + folder.count, 0);

// ---------------------------------------------------------------------------
// Filtering
// ---------------------------------------------------------------------------

/**
 * Demo line 2416, the `#tree-q` handler: a file matches when the query is a
 * substring of its label or of its machine list. A folder with no matching
 * file is dropped entirely, so the filtered tree is never a list of empty
 * headings.
 */
export function filterTree(tree: readonly TreeFolder[], query: string): TreeFolder[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...tree];

  const hit = (f: TreeFile) =>
    f.label.toLowerCase().includes(q) ||
    f.machines.some((m) => m.toLowerCase().includes(q));

  return tree
    .map((folder) => {
      const files = folder.files.filter(hit);
      const groups = folder.groups
        .map((g) => ({ ...g, files: g.files.filter(hit) }))
        .filter((g) => g.files.length > 0);
      const count = files.length + groups.reduce((a, g) => a + g.files.length, 0);
      return { ...folder, files, groups, count, defaultOpen: true };
    })
    .filter((folder) => folder.count > 0);
}

/**
 * Seed connector name → the label the lake catalogue files it under.
 *
 * Two vocabularies, and they are not the same strings. `seed/connectors.ts`
 * names the **software package** (`'XReach'`); `LakeEntry.conn` names the
 * **feed** (`'XReach · remote support'`), and several qualify further
 * (`'IBM Maximo · Meridian service register (all fleets)'`). Fuzzy matching
 * is not safe here — the two Maximo connectors share a leading segment and
 * would collide — so the correspondence is stated once, explicitly.
 *
 * `null` means the system has no folder of its own in the lake because its
 * data arrives as a table or a message rather than as files: work orders from
 * SAP PM, shift records from Pitram, the MQTT stream, the Salesforce tables,
 * the mail and meeting folders, the video transcripts and the single drill
 * plan. Those all appear in the tree, under the folder that holds their
 * format.
 *
 * The map runs one way only. `'Site profile register'` is a lake folder with
 * no connector card behind it, so it is reachable from the tree but not from
 * `/connectors`; that is the demo's own arrangement.
 */
export const LAKE_LABEL_FOR_CONNECTOR: Readonly<Record<string, string | null>> = {
  'IBM Maximo · service register': 'IBM Maximo · Meridian service register (all fleets)',
  'IBM Maximo · Gawler Deep EAM': 'IBM Maximo · Gawler Deep EAM (customer)',
  'Maximo Mobile · technician app': 'Maximo Mobile · technician app',
  RMS: 'RMS · Remote Monitoring Service',
  CatBase: 'CatBase · digital parts books',
  SharePoint: null,
  'OEM bulletin service': 'OEM bulletin service',
  XReach: 'XReach · remote support',
  'Oil analysis lab portal': 'Oil analysis lab portal',
  'Tyre management': 'Tyre management',
  LMS: 'LMS · training records',
  'Rostering / HR': 'Rostering / HR',
  'Customer satisfaction survey': 'Customer satisfaction survey (CSS)',
  'SAP PM': null,
  Pitram: null,
  'Underground Wi-Fi / LTE telematics': null,
  Salesforce: null,
  Outlook: null,
  'Microsoft Teams': null,
  'Training video & AR headset': null,
  'Rig control system': null,
};

/** Does this connector have a folder of its own in the lake? */
export const connectorHasFolder = (connector: string): boolean =>
  Boolean(LAKE_LABEL_FOR_CONNECTOR[connector]);

/**
 * `?conn=<name>` — narrow the tree to one connector's folder.
 *
 * A connector whose files live in the lake collapses the tree to that one
 * folder, opened. A connector that has no folder of its own, or a name that
 * matches nothing, leaves the tree whole; the scene says which case it is
 * rather than showing an empty pane.
 */
export function filterByConnector(
  tree: readonly TreeFolder[],
  connector: string | null,
): TreeFolder[] {
  if (!connector) return [...tree];
  const label = LAKE_LABEL_FOR_CONNECTOR[connector] ?? connector;
  const matched = tree.filter((f) => f.connector === label);
  if (matched.length === 0) return [...tree];
  return matched.map((f) => ({ ...f, defaultOpen: true }));
}

// ---------------------------------------------------------------------------
// Reference resolution
// ---------------------------------------------------------------------------

/** Prefixes `loadDocView` knows how to open. */
const KNOWN_PREFIXES = new Set([
  'doc',
  'eml',
  'mtg',
  'vtt',
  'tab',
  'rep',
  'x',
  'mqtt',
  'can',
  'xml',
  'plan',
  'py',
  'wo',
  'sap',
]);

/**
 * Demo line 2454, `openDoc(key)`. Normalise whatever a caller put in `?doc=`
 * (or the older `?ref=`) into a key this module can open.
 *
 * Six spellings arrive from four surfaces:
 *
 * | arriving as | from | resolves to |
 * |---|---|---|
 * | `eml:11` | `seed/alerts.ts`, `seed/incidents.ts` | the email whose file starts `11` |
 * | `eml:<file>.eml` | direct | itself |
 * | `mtg:MTG-2026-0904.txt` | `seed/incidents.ts` | itself |
 * | `doc:IR-2026-014` | `seed/incidents.ts` | the library document whose file starts with it |
 * | `x:XS-2049` | `seed/incidents.ts` | the lake file whose name starts with it |
 * | `plan:drillplan` | `seed/alerts.ts` | the drill-plan sample |
 * | a bare file name | `shared/assistant/sources.ts` | the library document, else the lake file |
 *
 * The demo's own two-digit special case is reproduced exactly: `openDoc` tests
 * `/^\d\d$/` on the sub-key and rewrites it to the matching `.eml` file.
 *
 * @returns the canonical key, or `null` when nothing matches.
 */
export function resolveDocKey(raw: string, ctx: TreeCtx): string | null {
  const value = raw.trim();
  if (!value) return null;

  const colon = value.indexOf(':');
  const prefix = colon > 0 ? value.slice(0, colon) : null;
  const rest = colon > 0 ? value.slice(colon + 1) : value;

  if (prefix && KNOWN_PREFIXES.has(prefix)) {
    // Demo line 2454 — `eml:11` names the email by its leading two digits.
    if (prefix === 'eml') {
      if (/^\d\d$/.test(rest)) {
        const email = ctx.comms.emails.find((e) => e.file.startsWith(rest));
        return email ? `eml:${email.file}` : null;
      }
      const exact = ctx.comms.emails.find(
        (e) => e.file === rest || e.file.startsWith(rest),
      );
      return exact ? `eml:${exact.file}` : null;
    }

    if (prefix === 'mtg') {
      const meeting = ctx.comms.meetings.find(
        (m) => m.transcript === rest || m.meeting_id === rest,
      );
      return meeting ? `mtg:${meeting.transcript}` : null;
    }

    if (prefix === 'doc') {
      const doc = ctx.docIndex.docs.find(
        (d) => d.file === rest || d.file.startsWith(rest),
      );
      return doc ? `doc:${doc.file}` : null;
    }

    if (prefix === 'x') {
      const entry = lakeEntryFor(rest, ctx.lake);
      return entry ? `x:${entry.file}` : null;
    }

    return value;
  }

  // No prefix: a bare file name. The assistant's source chips send these.
  const doc = ctx.docIndex.docs.find((d) => d.file === value || d.file.startsWith(value));
  if (doc) return `doc:${doc.file}`;

  const email = ctx.comms.emails.find((e) => e.file === value);
  if (email) return `eml:${email.file}`;

  const meeting = ctx.comms.meetings.find((m) => m.transcript === value);
  if (meeting) return `mtg:${meeting.transcript}`;

  const entry = lakeEntryFor(value, ctx.lake);
  return entry ? `x:${entry.file}` : null;
}

/** Demo line 2443 — exact file name first, then a prefix match. */
export function lakeEntryFor(name: string, lake: LakeIndex): LakeEntry | null {
  const slug = lake.by_file[name];
  if (slug) {
    const exact = lake.entries.find((e) => e.slug === slug);
    if (exact) return exact;
  }
  return lake.entries.find((e) => e.file.startsWith(name)) ?? null;
}

/** The demo's opening document: the work-order table (`openDoc('tab:wos')`). */
export const DEFAULT_DOC_KEY = 'tab:wos';
